import { useState } from "react";
import { router } from "@inertiajs/react";
import { X } from "lucide-react";
import TaskPeople from "@/pages/Tasks/components/TaskPeople";
import type { Person } from "@/pages/Tasks/task";
import TaskActivityFeed from "@/pages/Tasks/components/TaskActivityFeed";
import TaskCommentBox from "@/pages/Tasks/components/TaskCommentBox";
import { go_task_path, snooze_task_path } from "@/lib/routes";
import DueDates from "@/pages/Tasks/components/DueDates";
import {
  type Task, shortDateLabel, titleFor,
} from "@/pages/Tasks/task";

export function ContextRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex text-[13px]">
      <span className="w-[118px] shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 font-medium text-foreground">{children}</span>
    </div>
  );
}

const SECTION_HEAD = "text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground";
const QUIET_LINK = "underline underline-offset-[3px] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm";

// Details drawer (final UX direction §2): header with who's on it, then
// the action, then history. Leads with the same verb button as the row — never a dead
// end. No reminder counts, no state names, no stats.

export default function TaskDrawer({ task, people, onClose }: { task: Task; people: Person[]; onClose: () => void }) {
  const [feedVersion, setFeedVersion] = useState(0);

  return (
    <aside
      aria-label="Task details"
      className="flex h-full flex-col gap-4 overflow-hidden rounded-lg border border-border bg-background p-5 shadow-sm"
    >
        <div className="relative">
          <p className={SECTION_HEAD}>What to do</p>
          <h2 className="mt-1 text-lg font-bold leading-snug tracking-tight text-foreground">
            {titleFor(task)}
          </h2>
          <DueDates task={task} />
          <TaskPeople task={task} people={people} />
          {/* In review, the doer's instructions are done — show who the work
              waits on instead. The reviewer keeps their own framing (the
              "Review:" title above). */}
          {task.state === "submitted_for_review" && !task.can.approve ? (
            <p className="mt-2.5 text-[13px] font-medium leading-relaxed text-info">
              Waiting for {task.reviewers.map((r) => r.name).join(", ") || "review"} to review · submitted {shortDateLabel(task.state_entered_at.slice(0, 10))}
            </p>
          ) : (
            /* Projector-machinery rows carry description === details (the title
               is the whole instruction) — repeating it adds nothing. */
            task.description !== task.details && (
              <p className="mt-2.5 whitespace-pre-line text-[13px] leading-relaxed text-foreground">
                {task.description}
              </p>
            )
          )}
          <button
            type="button"
            aria-label="Close details"
            onClick={onClose}
            className="absolute right-0 top-0 rounded-sm p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X size={15} />
          </button>
        </div>

        {!task.completed_at && (
          <div className="flex items-center gap-2.5">
            <a
              href={go_task_path(task.id)}
              className="inline-flex h-9 items-center rounded-md bg-primary px-5 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {task.state === "submitted_for_review" ? "View submission" : "Go →"}
            </a>
          </div>
        )}
        {task.completed_at && task.has_review && (
          <div className="flex items-center gap-2.5">
            <a
              href={go_task_path(task.id)}
              className="inline-flex h-9 items-center rounded-md border border-border bg-background px-5 text-[13px] font-semibold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              View review
            </a>
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto border-t border-border pr-4 pt-4">
          <p className={SECTION_HEAD}>Activity</p>
          <TaskActivityFeed taskId={task.id} refreshKey={feedVersion} />
        </div>

        {/* Submitting happens on the plan page (the task widget's Submit),
            never from the board — the drawer's Go button is the doorway. */}
        <TaskCommentBox taskId={task.id} onPosted={() => setFeedVersion((v) => v + 1)} />

        {/* "Remind me later" (snooze) is hidden here on purpose — the row's
            menu still has it. Resume stays so a paused task can be unpaused. */}
        {task.can.resume && (
          // -mx-5/px-5 pulls the divider through the aside's padding so it
          // spans the full card width.
          <div className="-mx-5 flex items-center gap-4 border-t border-border px-5 pt-3.5 text-xs text-muted-foreground">
            <button type="button" onClick={() => router.delete(snooze_task_path(task.id), { preserveScroll: true })} className={QUIET_LINK}>
              Resume reminders
            </button>
          </div>
        )}

    </aside>
  );
}
